import { useState } from 'react';
import type { Deck, Pilot } from '@edh/shared';
import {
  bracketHandicap,
  deckLabel,
  display,
  isRanked,
  roman,
  skillOf,
  winChances,
} from '@edh/shared';
import { RankBadge } from './RankBadge';
import { RankedAvatar } from './RankedAvatar';
import { Sheet } from './Sheet';

/**
 * Who the table thinks is going to win, and by how much.
 *
 * Shown before the game so the result means something when it arrives: beating
 * a 70% favourite is a story, and beating a 51% one is a Tuesday. It is also
 * the honest face of the rating — the same numbers that will score the game,
 * stated out loud beforehand rather than only explaining themselves afterwards.
 */
export function TableOdds({
  seats,
  analysis,
}: {
  seats: { pilot: Pilot; deck: Deck }[];
  /** A written read on the matchup, once one has been written. */
  analysis?: string;
}) {
  const [info, setInfo] = useState(false);

  if (seats.length < 2) return null;

  const chances = winChances(
    seats.map((s) => ({
      // Skill, not the shown rating — see Contender. Otherwise the odds would
      // favour whoever has come to the most game nights.
      pilotElo: skillOf(s.pilot),
      deckElo: s.deck.elo,
      // So a deck that has not earned its seed does not tilt the line.
      deckGames: s.deck.placementGames,
      powerLevel: s.deck.powerLevel,
    })),
  );

  const ranked = seats
    .map((seat, i) => ({ ...seat, chance: chances[i]! }))
    .sort((a, b) => b.chance - a.chance);

  const favourite = ranked[0]!;
  const underdog = ranked[ranked.length - 1]!;
  const spread = Math.round((favourite.chance - underdog.chance) * 100);

  /**
   * Spec 10 — a rating off two games is not a rating, and odds built from four
   * of them are not odds. Named rather than hinted at: "still settling" told
   * you something was wrong without saying what, which is worse than silence.
   */
  const unsettled = [
    ...seats.filter((s) => !isRanked(s.pilot.placementGames)).map((s) => s.pilot.name),
    ...seats.filter((s) => !isRanked(s.deck.placementGames)).map((s) => deckLabel(s.deck)),
  ];

  /**
   * Where the gap between the two extremes comes from.
   *
   * The three numbers are the exact terms the rating will use, not a summary of
   * them — so "why" and "what it scored" are the same arithmetic. A market that
   * cannot show its working is just a number with a bar next to it.
   */
  const reasons = [
    {
      label: 'Player rating',
      value: favourite.pilot.elo - underdog.pilot.elo,
      detail: `${display(favourite.pilot.elo)} vs ${display(underdog.pilot.elo)}`,
    },
    {
      label: 'Deck rating',
      value: favourite.deck.elo - underdog.deck.elo,
      detail: `${display(favourite.deck.elo)} vs ${display(underdog.deck.elo)}`,
    },
    {
      label: 'Power level',
      value: bracketHandicap(favourite.deck.powerLevel, underdog.deck.powerLevel),
      detail:
        favourite.deck.powerLevel && underdog.deck.powerLevel
          ? `${roman(favourite.deck.powerLevel)} vs ${roman(underdog.deck.powerLevel)}`
          : 'not set',
    },
  ].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

  const biggest = reasons[0]!;

  return (
    <>
      <div className="section-head">
        <div className="section-head-title">
          <h2>Polymarket</h2>
          {/* The line and the score are the same arithmetic; this is where that
              gets said, rather than expanded inline under every table. */}
          <button
            className="section-info"
            onClick={() => setInfo(true)}
            aria-label="How the odds are scored"
          >
            i
          </button>
        </div>
      </div>

      <div className="panel">
        {ranked.map((seat) => (
          <div className="odds-row" key={seat.pilot.id}>
            <RankedAvatar pilot={seat.pilot} size={26} />
            <span className="odds-name">
              {seat.pilot.name}
              {isRanked(seat.pilot.placementGames) && (
                <RankBadge pilot={seat.pilot} size="small" />
              )}
            </span>
            <span className="odds-pct num">{Math.round(seat.chance * 100)}%</span>
            <div className="odds-track">
              <div className="odds-fill" style={{ width: `${seat.chance * 100}%` }} />
            </div>
          </div>
        ))}

        <p className="hint" style={{ margin: '12px 0 0' }}>
          {spread < 8 ? (
            <>Even table — nobody here is the underdog.</>
          ) : (
            <>
              <strong>{favourite.pilot.name}</strong> is favoured, {spread} points clear of{' '}
              <strong>{underdog.pilot.name}</strong>
              {biggest.value !== 0 && <> — mostly {biggest.label.toLowerCase()}</>}.
            </>
          )}
        </p>

        {/* Which ratings are not real yet, by name. */}
        {unsettled.length > 0 && (
          <p className="hint" style={{ margin: '8px 0 0' }}>
            {unsettled.join(', ')} {unsettled.length === 1 ? 'has' : 'have'} not finished
            placement, so this is closer to a guess than a prediction.
          </p>
        )}

        {/*
          The read, where the arithmetic used to be.
          
          A column of rating differences ending in a total said how big the edge
          was and nothing about where it came from - and the total was the least
          informative row of the lot, since it is just the sum of the three
          above it. This says the same thing in the terms people actually argue
          in: these decks, this matchup, what would have to happen.
        */}
        {analysis && (
          <div className="odds-read">
            <span className="kicker">The read</span>
            <p>{analysis}</p>
          </div>
        )}

      </div>

      {info && <ScoringInfoSheet onClose={() => setInfo(false)} />}
    </>
  );
}

/**
 * How the line is built, and why it is the same as the score.
 *
 * This is the on-demand version of what used to expand inline under every
 * table: the three ratings that set the odds are the three the game pays out
 * on, so "why is this the favourite" and "what did winning cost" are one
 * answer. General rather than per-matchup — the specific numbers live in the
 * bars above; this explains what they are.
 */
function ScoringInfoSheet({ onClose }: { onClose: () => void }) {
  return (
    <Sheet title="How the odds are scored" onClose={onClose}>
      <p className="hint" style={{ marginTop: -4 }}>
        The line is set from the same ratings that will score the game — said out loud
        beforehand, so the result means something when it lands.
      </p>

      <div className="guide-table">
        <div className="guide-row">
          <span className="guide-what">
            Player rating
            <span className="guide-note">
              each pilot’s skill on the ladder — not the shown rating, so turning up to the
              most game nights does not tilt the line
            </span>
          </span>
        </div>
        <div className="guide-row">
          <span className="guide-what">
            Deck rating
            <span className="guide-note">
              how the deck itself has fared; one still in placement counts for less so it
              does not tilt the line on a seed it has not earned
            </span>
          </span>
        </div>
        <div className="guide-row">
          <span className="guide-what">
            Power level
            <span className="guide-note">
              the bracket each deck is built to — a gap here handicaps the favourite
            </span>
          </span>
        </div>
      </div>

      <p className="hint" style={{ marginTop: 8 }}>
        Afterwards those same numbers move: beating a bigger favourite pays more, and a bigger
        table pays more again. The odds and the score are the same arithmetic.
      </p>
    </Sheet>
  );
}
